import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, switchMap, tap, catchError, EMPTY } from 'rxjs';
import { ConversationListComponent } from '../conversation-list/conversation-list.component';
import { AssistantChatComponent } from '../assistant-chat/assistant-chat.component';
import { AdminSessionSwitcherComponent } from '../../admin-session-switcher/admin-session-switcher.component';
import { PageHeaderComponent } from '../../common/page-header/page-header.component';
import { AssistantConversationService } from '../../chatbot/services/assistant-conversation.service';
import { ChatSessionService } from '../../chatbot/services/chat-session.service';
import { ChatUiStateService } from '../../chatbot/services/chat-ui-state.service';
import { ConversationSummary } from '../../chatbot/models/rag.models';
import { ChatMessage } from '../../chatbot/models/chat.models';

/**
 * Página completa del Asistente Virtual (superAdmin): sidebar de
 * conversaciones + área de chat. ChatSessionService/ChatUiStateService son
 * los mismos singletons que usa el widget flotante — decisión de diseño
 * (ver plan Fase 4): abrir esta página continúa la conversación que ya
 * estuviera activa, no fuerza una nueva.
 */
@Component({
  selector: 'app-assistant-page',
  standalone: true,
  imports: [
    TranslateModule,
    ConversationListComponent,
    AssistantChatComponent,
    AdminSessionSwitcherComponent,
    PageHeaderComponent
  ],
  templateUrl: './assistant-page.component.html',
  styleUrls: ['./assistant-page.component.scss']
})
export class AssistantPageComponent implements OnInit, OnDestroy {
  @ViewChild(ConversationListComponent) conversationList!: ConversationListComponent;

  activeConversationId: string | null = null;
  sidebarOpen = false;
  loadingConversation = false;

  private wasLoading = false;
  private readonly destroy$ = new Subject<void>();
  private readonly selectConversation$ = new Subject<ConversationSummary>();

  constructor(
    private conversationService: AssistantConversationService,
    private session: ChatSessionService,
    private uiStateService: ChatUiStateService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.activeConversationId = this.session.getConversationId();

    this.uiStateService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (this.wasLoading && !state.isLoading) {
          // Refresco silencioso: no debe hacer parpadear/vaciar la sidebar
          // en cada respuesta del asistente (ver plan, hallazgo I4).
          this.conversationList?.refresh(true);
          // Si era un chat nuevo, el backend recién asignó su conversationId
          // en esta respuesta — se sincroniza para que quede resaltado en la
          // sidebar en vez de aparecer sin selección tras el primer mensaje.
          this.activeConversationId = this.session.getConversationId();
        }
        this.wasLoading = state.isLoading;
      });

    // switchMap descarta automáticamente cualquier GET de una selección
    // anterior en cuanto llega una nueva (cancela la petición en vuelo y su
    // respuesta se ignora) — cierra la carrera de clicks rápidos en la
    // sidebar (hallazgo I3) sin bookkeeping manual de "último id pedido".
    this.selectConversation$
      .pipe(
        tap(() => this.loadingConversation = true),
        switchMap(item => this.conversationService.get(item.id).pipe(
          catchError(() => {
            this.loadingConversation = false;
            this.uiStateService.setError(this.translate.instant('assistantPage.chat.loadError'));
            return EMPTY;
          })
        )),
        takeUntil(this.destroy$)
      )
      .subscribe(detail => {
        const messages: ChatMessage[] = detail.messages.map(m => ({
          id: m.id,
          role: m.role,
          text: m.content,
          createdAt: m.createdAt
        }));
        this.session.loadConversation(detail.id, messages);
        this.activeConversationId = detail.id;
        this.sidebarOpen = false;
        this.loadingConversation = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectConversation(item: ConversationSummary): void {
    this.selectConversation$.next(item);
  }

  onNewChat(): void {
    this.session.newConversation();
    this.activeConversationId = null;
    this.sidebarOpen = false;
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }
}
